import { Plus, Play, ExternalLink } from "lucide-react";
import { Button } from "@shared/ui/Button";
import { TextInput } from "@shared/ui/TextInput";
import { Select } from "@shared/ui/Select";
import { Section } from "@shared/ui/Section";
import { Spinner } from "@shared/ui/Spinner";
import { Checkbox } from "@shared/ui/Checkbox";
import { FilePickerButton } from "@shared/ui/FilePickerButton";
import { addBlockClass } from "@shared/ui/inlineAction";
import { Badge } from "@shared/ui/Badge";
import { notify } from "@shared/ui/toast/toastStore";
import { PaneHeader } from "@features/pane-manager/PaneHeader";
import { PaneFooter } from "@features/pane-manager/PaneFooter";
import { usePaneManager } from "@features/pane-manager";
import { useScrollMemory } from "@features/pane-manager/useScrollMemory";
import {
    openConsole,
    consoleNextForSource,
    consoleNextForPortalSource,
} from "@features/handoff-to-console";
import { listProjects, updateProject, type Project } from "@entities/project";
import { listSources, uploadSource, type Source } from "@entities/source";
import {
    registerSlideFormat,
    commitSlideFormat,
    deleteSlideFormat,
    uploadViaPresignedPost,
    PPTX_MIME,
} from "@entities/slide-format";
import {
    createTask,
    updateTask,
    putTaskOverrides,
    getTaskBaseline,
    getTaskOverrides,
    getTask,
    type InputSelection,
} from "@entities/generation-task";
import {
    fetchPortalPreview,
    toRefItems,
    PortalInvalidUrlError,
    type PortalRefItem,
} from "@entities/portal";
import { generationSettingStore, mergePromptOverrides } from "@entities/generation-setting";
import { flushSettings, getCurrentFileBytes, getDocumentName } from "@features/office";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type Phase = "idle" | "loading" | "busy";
type PortalStatus = "idle" | "loading" | "invalid" | "error" | "ok";

/** 入力中の URL を引くまで待つ時間。Web コンソールの usePortalPreview と同じ（記録 18）。 */
const PORTAL_PREVIEW_DEBOUNCE_MS = 400;

/**
 * 生成が始まらなかったときに、使い捨てフォーマットを消す。
 *
 * **消せなくても生成開始の失敗を上書きしない。** 利用者に伝えるべきは「生成が始まらなかった」
 * ことで、後片付けの成否は二の次である。消せなかった場合は残るが、それは元の振る舞いと同じで
 * ある（記録 48）。
 */
async function discardTemporaryFormat(formatId: string): Promise<void> {
    try {
        await deleteSlideFormat(formatId);
    } catch {
        // 通信が切れている等。ここで投げると失敗の理由がすり替わる。
    }
}

/**
 * 開いている PPTX を、このタスク限りの使い捨てフォーマット（origin=task）として登録する。
 *
 * **独立実行と再生成で同じ道を通す。** サーバーが読むのは上げた PPTX の `im.prompts` なので、
 * どちらの経路でも「画面で見ている設定」と「実際に使われる設定」が一致する。経路ごとに上げる
 * ものを変えると、届く設定と届かない設定の一覧を利用者が覚えることになる。
 *
 * **実体を取り出す前に、設定の保存が出尽くすのを待つ。** 生成設定の書き込みは投げっぱなしなので、
 * 待たずに getFileAsync すると直前の編集が入っていない pptx を送りうる。保存が失敗していれば
 * 投げ、生成を始めない。
 *
 * 登録の途中で失敗したら、その場で写しを片付けてから投げる。呼び出し側は**タスクの作成に失敗
 * したとき**の片付けを持つ（記録 48）。
 */
async function uploadCurrentDocumentAsFormat(): Promise<string> {
    await flushSettings();
    const bytes = await getCurrentFileBytes();
    const docName = await getDocumentName();
    const blob = new Blob([bytes as BlobPart], { type: PPTX_MIME });
    const { slideFormat, upload } = await registerSlideFormat({
        name: docName || "format",
        filename: `${docName || "format"}.pptx`,
        size: bytes.byteLength,
        contentType: PPTX_MIME,
        origin: "task",
    });
    try {
        await uploadViaPresignedPost(upload, blob);
        await commitSlideFormat(slideFormat.id, { name: docName || "format" });
    } catch (e) {
        await discardTemporaryFormat(slideFormat.id);
        throw e;
    }
    return slideFormat.id;
}

export function GeneratePan() {
    const openPane = usePaneManager((s) => s.openPane);
    // 戻ってきたら離れたときの位置へ帰す（[[2026-09-04 設計ノート アドイン 戻ったときの表示位置]]）。
    const scroll = useScrollMemory();

    // 開いている PPTX が生成物なら、埋め込みの生成元がある＝連鎖できる。
    const sourceTaskId = generationSettingStore.getSourceTaskId();
    const sourceProjectId = generationSettingStore.getSourceProjectId();
    const canChain = Boolean(sourceTaskId && sourceProjectId);

    const [label, setLabel] = useState("");
    const [projects, setProjects] = useState<Project[]>([]);
    const [projectId, setProjectId] = useState("");
    const [sources, setSources] = useState<Source[]>([]);
    const [phase, setPhase] = useState<Phase>("idle");

    // 再生成扱い。連鎖できるときの既定は有効（設計ノートの決定）。
    const [regenerate, setRegenerate] = useState(canChain);

    // 除外指定。除外を並べる方式（空＝全件使用）でサーバーの入力選択と同じ形を保つ。
    const [excludedSourceIds, setExcludedSourceIds] = useState<string[]>([]);
    const [excludedPortalRefs, setExcludedPortalRefs] = useState<string[]>([]);

    // 社内ポータル連携。表示は保存済みではなく入力中 URL に追従させる（コンソールと同じ）。
    const [portalUrl, setPortalUrl] = useState("");
    const [portalProjectUrl, setPortalProjectUrl] = useState("");
    const [portalStatus, setPortalStatus] = useState<PortalStatus>("idle");
    const [portalItems, setPortalItems] = useState<PortalRefItem[]>([]);

    const loadedRef = useRef(false);

    const loadSources = useCallback(async (pid: string) => {
        try {
            setSources(await listSources(pid));
        } catch {
            setSources([]);
        }
    }, []);

    if (!loadedRef.current) {
        loadedRef.current = true;
        void (async () => {
            setPhase("loading");
            try {
                const list = await listProjects();
                setProjects(list);
                // 連鎖できるときは元タスクの案件を既定値にする。無ければ先頭。
                const initial =
                    (canChain && list.some((p) => p.id === sourceProjectId)
                        ? sourceProjectId
                        : list[0]?.id) ?? "";
                if (initial) {
                    setProjectId(initial);
                    applyProjectLink(list.find((p) => p.id === initial));
                    await loadSources(initial);
                }
                // 元タスクの入力選択を除外チェックの初期値へ反映する（未指定は全件へ戻るため明示的に運ぶ）。
                if (canChain) {
                    try {
                        const task = await getTask(sourceProjectId!, sourceTaskId!);
                        setExcludedSourceIds(task.inputSelection?.excludedSourceIds ?? []);
                        setExcludedPortalRefs(task.inputSelection?.excludedPortalRefs ?? []);
                    } catch {
                        // 元タスクが引けないときは全件から始める（連鎖自体は生成開始時に判定する）。
                    }
                }
            } catch (e) {
                notify(e instanceof Error ? e.message : "案件一覧の取得に失敗しました", "error");
            }
            setPhase("idle");
        })();
    }

    /** 選択した案件の連携先を URL 欄へ写す（未連携は空）。 */
    function applyProjectLink(project: Project | undefined) {
        setPortalUrl(project?.portalUrl ?? "");
        setPortalProjectUrl(project?.portalProjectUrl ?? "");
    }

    // 入力中 URL のライブプレビュー。企業・案件のどちらでも同じ経路で引き、結果を統合して並べる。
    //
    // **URL の変更は 400ms 待ってから引く。** 入力欄の値をそのまま依存に置いていたため、1 文字
    // 打つたびに社内ポータルへ問い合わせていた（記録 18）。手で打てば数十回になる。IM 側ではなく
    // 連携先の負荷なので、相手の制限に当たると下読みが不安定に見える。Web コンソールの
    // `usePortalPreview` が同じ機能を同じ 400ms で間引いているので、そちらへ揃えた。
    //
    // state の更新はタイマーと取得の継続の中だけで行い、効果の本体では更新しない。古い応答は
    // `alive` で捨てる（早く打ったときの入れ替わり）。
    useEffect(() => {
        const url = portalProjectUrl.trim() || portalUrl.trim();
        let alive = true;
        const timer = setTimeout(() => {
            if (!url) {
                setPortalStatus("idle");
                setPortalItems([]);
                return;
            }
            setPortalStatus("loading");
            void (async () => {
                try {
                    const ref = await fetchPortalPreview(url);
                    if (!alive) return;
                    setPortalItems(toRefItems(ref));
                    setPortalStatus("ok");
                } catch (e) {
                    if (!alive) return;
                    setPortalItems([]);
                    setPortalStatus(e instanceof PortalInvalidUrlError ? "invalid" : "error");
                }
            })();
        }, PORTAL_PREVIEW_DEBOUNCE_MS);
        return () => {
            alive = false;
            clearTimeout(timer);
        };
    }, [portalUrl, portalProjectUrl]);

    async function handleProjectChange(value: string) {
        setProjectId(value);
        if (value) {
            applyProjectLink(projects.find((p) => p.id === value));
            await loadSources(value);
        } else {
            applyProjectLink(undefined);
            setSources([]);
        }
    }

    async function handleAddFiles(files: File[]) {
        const pid = projectId || null;
        if (!pid) {
            notify("先に案件を選択してください", "error");
            return;
        }
        for (const file of files) {
            try {
                await uploadSource(pid, file);
            } catch (e) {
                notify(
                    e instanceof Error ? e.message : `${file.name} のアップロードに失敗しました`,
                    "error"
                );
            }
        }
        await loadSources(pid);
    }

    function toggleSource(id: string) {
        setExcludedSourceIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    }

    function togglePortalRef(ref: string) {
        setExcludedPortalRefs((prev) =>
            prev.includes(ref) ? prev.filter((x) => x !== ref) : [...prev, ref]
        );
    }

    const readySources = useMemo(() => sources.filter((s) => s.status === "ready"), [sources]);
    const usableSources = useMemo(
        () => readySources.filter((s) => !excludedSourceIds.includes(s.id)),
        [readySources, excludedSourceIds]
    );
    /**
     * 生成の入力になる社内ポータルの項目。
     *
     * **ポータルだけでも入力は成り立つ。** 開始の瞬間にサーバーが項目を受領データへ書き起こす
     * ため、ファイルの受領データが 1 件も無くても生成できる。ここを数えていなかったので、
     * 資料をポータル連携だけでそろえた案件では生成開始のボタンが押せなかった（記録 68）。
     * 参照を持たない項目は除外できない＝常に使うので、除外の判定は参照があるものだけに効かせる
     * （コンソールの `hasDraftPortalInput` と同じ形）。
     */
    const usablePortalItems = useMemo(
        () =>
            portalStatus === "ok"
                ? portalItems.filter((i) => !i.ref || !excludedPortalRefs.includes(i.ref))
                : [],
        [portalStatus, portalItems, excludedPortalRefs]
    );
    /** 生成の根拠になるものが 1 つでもあるか。再生成は元タスクの入力を引き継ぐので問わない。 */
    const hasInput = usableSources.length > 0 || usablePortalItems.length > 0;

    /** 生成ボタン押下時の分岐。選択案件が元案件と一致し、かつ再生成扱いなら連鎖として組み立てる。 */
    const chaining = canChain && regenerate && projectId === sourceProjectId;

    async function handleGenerate() {
        setPhase("busy");
        try {
            const inputSelection: InputSelection = { excludedSourceIds, excludedPortalRefs };

            if (chaining) {
                // 再生成: 開いている PPTX をフォーマットとして上げる → 作成 → 手元の調整を
                // 届ける → 開始。
                //
                // **本体を上げるのは、編集用を直したものをフォーマットとして使えるようにする
                // ため。** アドインの設定は PPTX の `im.prompts` にしか無く、上書きが運べるのは
                // 指示の本文と skip だけである。上げないと、対象の付け替え・枠ごとの生成しない
                // 指定・要素とスライドの設定・担当の追加と削除・PowerPoint 上での紙面の変更が
                // すべて落ち、前回の設定で実行される。編集用をそのまま使う場合も同じ中身が
                // 上がってくるだけなので、経路を分けない。
                //
                // `sourceTaskId` は引き続き載せる。系譜と、上書きの引き継ぎ元がこれで決まる。
                // サーバーは**フォーマットがあればそれをテンプレートとして採る**。
                const formatId = await uploadCurrentDocumentAsFormat();
                const payload = generationSettingStore.getPayload();
                let task;
                try {
                    task = await createTask(sourceProjectId!, {
                        sourceTaskId: sourceTaskId!,
                        slideFormatId: formatId,
                        label: label || undefined,
                        inputSelection,
                    });
                } catch (e) {
                    await discardTemporaryFormat(formatId);
                    throw e;
                }
                // **本文と skip は上書きで届ける。** タスクの prompts は縮約されて本文が落ちる
                // ため、そこへ載せても実行には効かない（[[prompt_overlay]] の器の分担）。
                // 落ちたまま開始すると、直したはずの本文が前回のまま使われる。
                //
                // 送るのは規定の写しとの**差分**である。上書きは元タスクから引き継がれる器で、
                // コンソールで直した本文が入っている。手元の値で丸ごと置き換えると、アドインには
                // 見えていないその調整が黙って消える。
                //
                // 書き込みは全置換なので、引き継がれた分を**読んで重ねる**。差分だけを送ると、
                // 引き継がれたコンソール側の調整がそこで消える。重ね方は
                // [[mergePromptOverrides]] が持つ（skip だけは手元を正とする）。
                const baseline = await getTaskBaseline(sourceProjectId!, sourceTaskId!);
                const carried = await getTaskOverrides(sourceProjectId!, task.id);
                await putTaskOverrides(
                    sourceProjectId!,
                    task.id,
                    mergePromptOverrides(carried.prompts, payload, baseline?.assignments ?? {}),
                    baseline?.fingerprint ?? carried.baselineFingerprint ?? ""
                );
                // 縮約された写しは表示のために置く（担当名と対応表）。実行の値は上書きが持つ。
                await updateTask(sourceProjectId!, task.id, { prompts: payload }, { run: true });
                notify("元のタスクの続きとして生成を開始しました", "success");
            } else {
                const pid = projectId;
                if (portalUrl.trim() || portalProjectUrl.trim()) {
                    // 空欄は送られない（`updateProject` が空を落とす）ので、片方を空にしても
                    // その連携は保たれる。**サーバーは空文字を「連携解除」と読む**ので、
                    // 落とすのをやめると生成開始のついでに連携が外れる（記録 33）。
                    // アドインからの解除は行わない方針で、解除はコンソールの編集に委ねている。
                    await updateProject(pid, { portalUrl, portalProjectUrl });
                }
                // 独立実行: 現在の PPTX を使い捨てフォーマット（origin=task）として登録する。
                // 一覧に出ず、タスク削除で連鎖削除されるためライブラリーを汚さない。
                const formatId = await uploadCurrentDocumentAsFormat();
                let task;
                try {
                    // **作成と開始を分ける。** 社内ポータルの項目を受領データへ書き起こすのは
                    // 開始（`?run=true` の更新）だけで、作成と同時に開始する経路では走らない。
                    // 1 発で済ませていたため、資料をポータル連携だけでそろえた案件は
                    // 「生成の根拠となる受領データがありません」で弾かれていた（記録 68）。
                    // コンソールも作成（run なし）→ 開始の 2 段で、この順にすれば通る。
                    task = await createTask(pid, {
                        slideFormatId: formatId,
                        label: label || undefined,
                        inputSelection,
                    });
                } catch (e) {
                    await discardTemporaryFormat(formatId);
                    throw e;
                }
                // ここから先は写しを片付けない。**タスクが持っているので、消すとタスクが
                // 参照先を失う。** 開始できなかったタスクは一覧に出るので、利用者が消せば
                // 写しも連鎖で消える（記録 48 の「誰からも見えない残り物」にはならない）。
                try {
                    await updateTask(pid, task.id, {}, { run: true });
                } catch (e) {
                    const detail = e instanceof Error ? e.message : "原因を特定できませんでした";
                    throw new Error(
                        `タスクは作成しましたが、生成を開始できませんでした: ${detail}`
                    );
                }
                notify("生成を開始しました", "success");
            }
            setLabel("");
        } catch (e) {
            notify(e instanceof Error ? e.message : "生成開始に失敗しました", "error");
        }
        setPhase("idle");
    }

    // 案件の新規作成は Web コンソールが持つ。アドインはリソースUI主体で、作成のような
    // 段取りの要る操作は handoff する（[[アドイン開発規則]]）。ここへ選択肢だけ置くと、
    // 受領データを付けられないまま生成へ進めない行き止まりになる。
    const projectOptions = projects.map((p) => ({ value: p.id, label: p.name }));

    const portalSummary =
        portalStatus === "ok"
            ? `連携対象 ${portalItems.length} 件`
            : portalStatus === "loading"
              ? "URL を確認しています…"
              : "";

    return (
        <>
            <PaneHeader title="スライド生成" />
            <div ref={scroll.ref} className="flex-1 overflow-y-auto">
                <div className="p-4">
                    <TextInput
                        value={label}
                        onChange={setLabel}
                        placeholder="タスク名（任意）"
                        fullWidth
                        size="lg"
                        className="border-transparent bg-transparent focus:border-interactive"
                    />
                </div>

                <div className="px-4 pb-4">
                    <Section title="案件">
                        {phase === "loading" ? (
                            <div className="flex items-center gap-2 py-2 text-sm text-text-muted">
                                <Spinner /> 読み込み中...
                            </div>
                        ) : (
                            <>
                                {projects.length === 0 ? (
                                    <div className="py-1">
                                        <p className="text-[13px] text-text-muted">
                                            案件がまだありません。
                                        </p>
                                        <button
                                            type="button"
                                            className="mt-1 flex items-center gap-1 text-[13px] text-interactive hover:underline"
                                            onClick={() => void openConsole({})}
                                        >
                                            Web コンソールで案件を作成
                                            <ExternalLink size={12} />
                                        </button>
                                    </div>
                                ) : (
                                    <Select
                                        value={projectId}
                                        onChange={(v) => void handleProjectChange(v)}
                                        options={projectOptions}
                                        fullWidth
                                    />
                                )}
                                {canChain && projectId === sourceProjectId && (
                                    <p className="py-1 text-[11px] text-text-muted">
                                        元タスクの案件
                                    </p>
                                )}
                            </>
                        )}
                    </Section>
                </div>

                <div className="px-4 pb-4">
                    <Section title="フォーマット">
                        {chaining ? (
                            <p className="py-1 text-[13px]">元タスクから引き継ぎ</p>
                        ) : (
                            <div className="flex items-center gap-1.5 py-1">
                                <span className="shrink-0 rounded bg-sidebar-bg px-1.5 py-0.5 text-[11px] font-semibold text-text-muted">
                                    PPTX
                                </span>
                                <span className="text-[13px]">現在のドキュメント</span>
                            </div>
                        )}
                    </Section>
                </div>

                <div className="px-4 pb-4">
                    <Section title="社内ポータル連携">
                        <div className="flex items-center gap-1.5">
                            <span className="w-7 shrink-0 text-[11px] text-text-secondary">
                                企業
                            </span>
                            <TextInput
                                value={portalUrl}
                                onChange={setPortalUrl}
                                placeholder="企業ページ URL"
                                fullWidth
                            />
                        </div>
                        <div className="mt-1.5 flex items-center gap-1.5">
                            <span className="w-7 shrink-0 text-[11px] text-text-secondary">
                                案件
                            </span>
                            <TextInput
                                value={portalProjectUrl}
                                onChange={setPortalProjectUrl}
                                placeholder="案件ページ URL"
                                fullWidth
                            />
                        </div>
                        {portalSummary && (
                            <p className="py-1 text-[11px] text-text-muted">{portalSummary}</p>
                        )}
                        {portalStatus === "invalid" && (
                            <p className="py-1 text-[11px] text-danger">
                                社内ポータルのページ URL として認識できません。
                            </p>
                        )}
                        {portalStatus === "error" && (
                            <p className="py-1 text-[11px] text-danger">
                                社内ポータルの取得に失敗しました。
                            </p>
                        )}
                        {portalItems.map((item) => {
                            const next = consoleNextForPortalSource(projectId || null, item.ref);
                            const excluded = item.ref
                                ? excludedPortalRefs.includes(item.ref)
                                : false;
                            return (
                                <div key={item.key} className="flex items-center gap-1.5 py-1">
                                    <Checkbox
                                        checked={!excluded}
                                        disabled={!item.ref}
                                        onChange={() => {
                                            if (item.ref) togglePortalRef(item.ref);
                                        }}
                                        ariaLabel={`${item.label} を生成に使う`}
                                    />
                                    <span className="shrink-0 rounded bg-sidebar-bg px-1 py-0.5 text-[10px] text-text-muted">
                                        {item.origin}
                                    </span>
                                    {next ? (
                                        <button
                                            type="button"
                                            className="min-w-0 flex-1 truncate text-left text-[13px] text-interactive hover:underline"
                                            onClick={() => void openConsole({ next })}
                                        >
                                            {item.label}
                                        </button>
                                    ) : (
                                        <span className="min-w-0 flex-1 truncate text-[13px]">
                                            {item.label}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </Section>
                </div>

                <div className="px-4 pb-4">
                    <Section title="受領データ">
                        <FilePickerButton
                            onPick={handleAddFiles}
                            multiple
                            ariaLabel="受領データを追加"
                            className={`${addBlockClass} mb-1.5`}
                        >
                            <Plus size={12} />
                            ファイルを追加
                        </FilePickerButton>
                        {sources.length === 0 && (
                            <p className="py-2 text-xs text-text-muted">受領データがありません</p>
                        )}
                        {sources.map((s) => {
                            const ready = s.status === "ready";
                            const excluded = excludedSourceIds.includes(s.id);
                            return (
                                <div key={s.id} className="flex items-center gap-1.5 py-1">
                                    {/* 準備中は生成の対象にできないため操作させない。 */}
                                    <Checkbox
                                        checked={ready && !excluded}
                                        disabled={!ready}
                                        onChange={() => toggleSource(s.id)}
                                        ariaLabel={`${s.name} を生成に使う`}
                                    />
                                    <Badge variant={ready ? "success" : "brand"}>
                                        {ready ? "完了" : "処理中"}
                                    </Badge>
                                    {ready ? (
                                        <button
                                            type="button"
                                            className="min-w-0 flex-1 truncate text-left text-[13px] text-interactive hover:underline"
                                            onClick={() =>
                                                void openConsole({
                                                    next: consoleNextForSource(s.id),
                                                })
                                            }
                                        >
                                            {s.name}
                                        </button>
                                    ) : (
                                        <span className="min-w-0 flex-1 truncate text-[13px]">
                                            {s.name}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </Section>
                </div>

                {/* 生成元。開いている PPTX に埋め込まれた元タスク由来の情報をここへ集約する
            （生成結果への導線と再生成の可否は同じデータ群）。 */}
                <div className="px-4 pb-4">
                    <Section title="生成元">
                        {canChain ? (
                            <>
                                <button
                                    type="button"
                                    className="flex items-center gap-1 py-1 text-[13px] text-interactive hover:underline"
                                    onClick={() => openPane("results")}
                                >
                                    生成結果を開く（根拠・要確認事項）
                                    <ExternalLink size={12} />
                                </button>
                                <div className="flex items-start gap-1.5 py-1">
                                    <Checkbox
                                        checked={regenerate}
                                        onChange={() => setRegenerate((v) => !v)}
                                        ariaLabel="元のタスクの続きとして生成する"
                                    />
                                    <span className="text-[13px]">
                                        元のタスクの続きとして生成する
                                        {projectId !== sourceProjectId && (
                                            <span className="block text-[11px] text-text-muted">
                                                案件を変更したため、独立したタスクとして実行します
                                            </span>
                                        )}
                                    </span>
                                </div>
                            </>
                        ) : (
                            <div className="flex items-start gap-1.5 py-1">
                                <Checkbox
                                    checked={false}
                                    disabled
                                    onChange={() => undefined}
                                    ariaLabel="元のタスクの続きとして生成する"
                                />
                                <span className="text-[13px] text-text-muted">
                                    元のタスクの続きとして生成する
                                    <span className="block text-[11px]">
                                        この PPTX に元タスクの情報がありません
                                    </span>
                                </span>
                            </div>
                        )}
                    </Section>
                </div>
            </div>
            <PaneFooter>
                <span className="flex-1" />
                <Button
                    variant="primary"
                    disabled={phase === "busy" || (!chaining && !hasInput)}
                    leftIcon={phase === "busy" ? <Spinner /> : <Play size={14} />}
                    onClick={() => void handleGenerate()}
                >
                    {phase === "busy" ? "処理中..." : "生成開始"}
                </Button>
            </PaneFooter>
        </>
    );
}
